/* eslint-disable node/prefer-global/process */
import type { SearchListSuccess } from '@autobid/strapi-integration/typescript/auction/searchCars'
import type {
  ItemListFiltersStateKey,
} from '@autobid/ui/composables/carsList/useFilter'
import type {
  SortBy,
  SortByItem,
} from '@autobid/ui/types/components/CarsList'
import type { UseQueryOptions } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useSearchCarsSocket } from '@autobid/ui/composables/car/sockets/useSearchCarsSocket'
import {
  useCarsListUrlFilter,
} from '@autobid/ui/composables/carsList/useFilter'
import { SEARCH_LIST_DEFAULT_SORTING_TYPE } from '@autobid/ui/constants/carsList/SEARCH_LIST_DEFAULT_SORTING_TYPE'
import { prepareMultiErrorCause } from '@autobid/ui/utils/auctions/prepareMultiErrorCause'
import { generateImageSizesFromObject } from '@autobid/ui/utils/car/generateImageSizes'
import {
  getRange,
  getSortBy,
} from '@autobid/ui/utils/prepareCarsListFiltersUtils'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, unref } from 'vue'

// #region constants
const CACHE_TIME = 1000 * 60 * 60

export const LIST_OF_SUPPORTED_SEARCH_LIST_FILTERS: ItemListFiltersStateKey[]
  = [
    'currentPage',
    'sortingType',
    'pageType',
    'e174',
    'category',
    'manufacturerModel',
    'e17',
    'e223',
    'e19from',
    'e19to',
    'e68from',
    'e68to',
    'e21from',
    'e21to',
    'e31',
    'e70',
    'e24',
    'earliestDateOfAddition',
  ]
// #endregion

// #region types
interface RangeFilterValue {
  valueFrom: number | null
  valueTo: number | null
}

export interface SearchCarsApiFilters {
  highlighted?: boolean
  pageSize: number
  pageNumber: number
  lang: string
  sortBy: SortBy
  ids?: number[]
  e174?: boolean
  earliestDateOfAddition?: 'today' | 'last_24H' | 'last_48H' | 'last_72H'
  /**
   * @description number_1;number_2 - where number_1 is an id of the parent category e.g. id of PKW and number_2 is an id of the child category e.g id of Kombi
   * @example ['1;2', '1;3']
   */
  typeCategory?: string[]
  /**
   * @description manufacturerId;model
   * @example ['1;E46'] - is going to search for a BMW E46
   */
  manufacturerModel?: string[]
  e17?: number[]
  e24?: number
  e70?: number
  e31?: number[]
  e21?: RangeFilterValue
  e68?: RangeFilterValue
  e19?: RangeFilterValue
  e223?: number[]
}

interface Options {
  pageSize: number
  highlighted?: boolean
}
// #endregion

// #region internal functions
function prepareCarData(data: SearchListSuccess['data']['items']) {
  const items = data.items.map((car) => {
    return {
      ...car,
      imageGroups: generateImageSizesFromObject('hd', car.imageGroups),
    }
  })

  return {
    ...data,
    items,
  }
}
// #endregion

export const searchCarsQueryKey = {
  all: () => ['search-results'] as const,
  filtered: (filters: SearchCarsApiFilters, highlighed: boolean) => ['search-results', filters, highlighed] as const,
}

export function useSearchCars(ids: MaybeRef<number[] | undefined>, { pageSize, highlighted }: Options, options?: UseQueryOptions<SearchListSuccess['data']['items'] | null, Error>) {
  const { locale } = useI18n()
  const { $trpc } = useNuxtApp()
  const { init: initSockets } = useSearchCarsSocket()

  const {
    filters: {
      currentPage,
      sortingType,
      e174,
      earliestDateOfAddition,
      category,
      manufacturerModel,
      e17,
      e24,
      e70,
      e31,
      e21from,
      e21to,
      e68from,
      e68to,
      e19from,
      e19to,
      e223,
    },
  }
    = useCarsListUrlFilter()

  const filters = computed((): SearchCarsApiFilters => {
    const [sortName, sortOrder] = (
      sortingType.value || SEARCH_LIST_DEFAULT_SORTING_TYPE
    ).split('-')

    return {
      highlighted,
      pageSize,
      pageNumber: (currentPage.value || 1) - 1,
      lang: locale.value,
      sortBy: getSortBy(
        sortName as SortByItem['name'],
        sortOrder as SortByItem['order'],
      ),
      ids: unref(ids),
      e174: e174.value ? true : undefined,
      earliestDateOfAddition: earliestDateOfAddition.value,
      typeCategory: category.value,
      manufacturerModel: manufacturerModel.value,
      e17: e17.value,
      e24: e24.value,
      e70: e70.value,
      e31: e31.value,
      e21: getRange(e21from.value, e21to.value),
      e68: getRange(e68from.value, e68to.value),
      e19: getRange(e19from.value, e19to.value),
      e223: e223.value,
    }
  })

  const queryKey = computed(() =>
    searchCarsQueryKey.filtered(
      unref(filters),
      Boolean(highlighted),
    ),
  )

  const queryFn = async (): Promise<SearchListSuccess['data']['items']> => {
    const unrefedIds = unref(ids)
    if (unrefedIds && !unrefedIds.length) {
      return {
        itemPageCount: 0,
        metadata: {
          errors: [],
          counters: {},
        },
        items: [],
      }
    }

    const result = await $trpc.auctions.getSearchCars.mutate(filters.value)

    if (!result) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No response',
      })
    }

    if ('errors' in result) {
      throw createError({
        statusCode: result.errors[0].status_code,
        statusMessage: result.errors[0].message,
        cause: prepareMultiErrorCause(result.errors),
      })
    }

    if (!result.data.items) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No response',
      })
    }

    return prepareCarData(result.data.items)
  }

  onMounted(initSockets)

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await queryFn()
      }
      catch (error) {
        const { message, code, handled } = getError(error)

        if (!handled) {
          throw createError({ statusCode: code, statusMessage: message })
        }

        return null
      }
    },
    staleTime: CACHE_TIME,
    ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    refetchOnWindowFocus: false,
    ...(options || {}),
  })

  return {
    ...query,
    queryKey,
  }
}
